/**
 * Twitter/X Ads CSV Writer v1
 *
 * Generates CSV files compatible with Twitter Ads Editor bulk upload.
 *
 * @see https://business.twitter.com/en/help/campaign-editing-and-optimization/ads-editor.html
 */

import { CampaignData, PlatformWriter, ValidationResult } from '../../types';

/**
 * Mapping from canonical objectives to Twitter objective codes
 */
const TWITTER_OBJECTIVES: Record<string, string> = {
  awareness: 'AWARENESS',
  brand_awareness: 'AWARENESS',
  reach: 'AWARENESS',
  traffic: 'WEBSITE_CLICKS',
  engagement: 'ENGAGEMENTS',
  leads: 'WEBSITE_CLICKS',
  sales: 'WEBSITE_CLICKS',
  conversions: 'WEBSITE_CLICKS',
  video_views: 'VIDEO_VIEWS',
  followers: 'FOLLOWERS',
  app_installs: 'APP_INSTALLS',
};

/**
 * Twitter Ads CSV column headers
 */
const TWITTER_HEADERS = [
  'Campaign Name',
  'Campaign Objective',
  'Campaign Status',
  'Campaign Daily Budget',
  'Campaign Total Budget',
  'Campaign Start Time',
  'Campaign End Time',
  'Ad Group Name',
  'Ad Group Status',
  'Ad Group Bid Amount',
  'Ad Group Bid Strategy',
  'Ad Group Placements',
  'Targeting Gender',
  'Targeting Age',
  'Targeting Locations',
  'Targeting Languages',
  'Tweet Text',
  'Media URL',
  'Media Type',
  'Card URL',
];

export class TwitterWriterV1 implements PlatformWriter {
  readonly platform = 'twitter' as const;
  readonly version = 'v1';
  readonly formatName = 'Twitter Ads Editor Import';

  validate(data: CampaignData): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Required fields
    if (!data.title) {
      errors.push('Campaign name is required');
    }

    // Budget validation
    if (!data.budget?.daily && !data.budget?.lifetime) {
      errors.push('Either daily or total budget is required');
    }

    // Tweet text validation
    const tweetText = this.composeTweetText(data);
    if (!tweetText) {
      errors.push('Tweet text is required');
    }
    if (tweetText.length > 280) {
      errors.push(
        `Tweet text is ${tweetText.length} characters. Maximum is 280.`
      );
    }

    // Character limit warnings
    if (tweetText.length > 250) {
      warnings.push(
        `Tweet text is ${tweetText.length} characters. Shorter tweets often perform better.`
      );
    }

    // Media warnings
    if (!data.media?.length) {
      warnings.push(
        'No media attached. Tweets with images or videos typically get higher engagement.'
      );
    }

    // Landing page for website clicks
    const objective = TWITTER_OBJECTIVES[data.objective?.toLowerCase()] || 'WEBSITE_CLICKS';
    if (objective === 'WEBSITE_CLICKS' && !data.ad_copy?.link_url) {
      errors.push('Landing page URL is required for website clicks objective');
    }

    // Funding instrument warning
    if (!data.platform_specific?.twitter?.funding_instrument_id) {
      warnings.push(
        'Funding Instrument ID not provided. You will need to set this in Twitter Ads Manager.'
      );
    }

    return { valid: errors.length === 0, errors, warnings };
  }

  transform(data: CampaignData): Record<string, string>[] {
    const rows: Record<string, string>[] = [];

    const objective =
      TWITTER_OBJECTIVES[data.objective?.toLowerCase()] || 'WEBSITE_CLICKS';

    // Format dates (Twitter expects ISO 8601)
    const startTime = this.formatDateTime(data.timeline?.start_date);
    const endTime = data.timeline?.end_date
      ? this.formatDateTime(data.timeline.end_date)
      : '';

    // Check if we have full campaigns structure
    const twitterCampaigns = data.campaigns?.filter(c => c.platform === 'twitter') || [];

    if (twitterCampaigns.length > 0 && twitterCampaigns.some(c => c.ad_groups && c.ad_groups.length > 0)) {
      // Use full campaigns structure - generate one row per ad variation
      for (const campaign of twitterCampaigns) {
        const campaignName = campaign.name || data.title;
        const campaignObjective = TWITTER_OBJECTIVES[campaign.objective?.toLowerCase()] || objective;

        for (const adGroup of campaign.ad_groups || []) {
          // Parse targeting from ad group
          const adGroupTargeting = adGroup.targeting || {};
          const locations = adGroupTargeting.locations?.join(';') ||
            data.targeting?.locations?.map((l) => l.country).join(';') || 'US';

          // Parse age range
          let ageMin = data.targeting?.age_min;
          let ageMax = data.targeting?.age_max;
          if (adGroupTargeting.age_range) {
            if (typeof adGroupTargeting.age_range === 'object') {
              ageMin = adGroupTargeting.age_range.min;
              ageMax = adGroupTargeting.age_range.max;
            } else if (typeof adGroupTargeting.age_range === 'string') {
              const parts = adGroupTargeting.age_range.split('-');
              ageMin = parseInt(parts[0]) || undefined;
              ageMax = parseInt(parts[1]) || undefined;
            }
          }
          const age = this.formatAgeRange(ageMin, ageMax);

          // Gender formatting
          const genderStr = adGroupTargeting.gender?.toLowerCase();
          const gender = genderStr === 'male' ? 'MALE' :
            genderStr === 'female' ? 'FEMALE' : 'ANY';

          const languages = data.targeting?.languages?.join(';') || 'en';

          for (let adIdx = 0; adIdx < (adGroup.ad_variations?.length || 0); adIdx++) {
            const ad = adGroup.ad_variations[adIdx];

            // Compose tweet text from ad data
            const tweetText = this.composeTweetTextFromAd(ad, data);

            // Get media for this ad
            let mediaUrl = '';
            let mediaType = '';
            if (ad.media && ad.media.length > 0) {
              const mediaAsset = ad.media[0];
              mediaUrl = mediaAsset?.public_url || '';
              mediaType = mediaAsset?.type === 'video' ? 'VIDEO' : 'IMAGE';
            } else {
              const mediaAsset = data.media?.[0];
              mediaUrl = mediaAsset?.public_url || '';
              mediaType = mediaAsset?.type === 'video' ? 'VIDEO' : 'IMAGE';
            }

            // Get landing page URL
            const cardUrl = ad.link_url || ad.landing_page || data.ad_copy?.link_url || '';

            rows.push({
              'Campaign Name': campaignName,
              'Campaign Objective': campaignObjective,
              'Campaign Status': 'PAUSED',
              'Campaign Daily Budget': data.budget?.daily?.toFixed(2) || '',
              'Campaign Total Budget': data.budget?.lifetime?.toFixed(2) || '',
              'Campaign Start Time': startTime,
              'Campaign End Time': endTime,
              'Ad Group Name': adGroup.name || `${campaignName} - Ad Group`,
              'Ad Group Status': 'PAUSED',
              'Ad Group Bid Amount': '1.50',
              'Ad Group Bid Strategy': 'AUTO',
              'Ad Group Placements': 'ALL_ON_TWITTER',
              'Targeting Gender': gender,
              'Targeting Age': age,
              'Targeting Locations': locations,
              'Targeting Languages': languages,
              'Tweet Text': tweetText,
              'Media URL': mediaUrl,
              'Media Type': mediaType,
              'Card URL': cardUrl,
            });
          }
        }
      }
    }

    // Fallback: If no rows generated from campaigns structure, use legacy single-row approach
    if (rows.length === 0) {
      const tweetText = this.composeTweetText(data);
      const mediaAsset = data.media?.[0];
      const mediaType = mediaAsset?.type === 'video' ? 'VIDEO' : 'IMAGE';
      const gender = this.formatGender(data.targeting?.genders);
      const age = this.formatAgeRange(
        data.targeting?.age_min,
        data.targeting?.age_max
      );
      const locations =
        data.targeting?.locations?.map((l) => l.country).join(';') || 'US';
      const languages = data.targeting?.languages?.join(';') || 'en';

      rows.push({
        'Campaign Name': data.title,
        'Campaign Objective': objective,
        'Campaign Status': 'PAUSED',
        'Campaign Daily Budget': data.budget?.daily?.toFixed(2) || '',
        'Campaign Total Budget': data.budget?.lifetime?.toFixed(2) || '',
        'Campaign Start Time': startTime,
        'Campaign End Time': endTime,
        'Ad Group Name': `${data.title} - Ad Group`,
        'Ad Group Status': 'PAUSED',
        'Ad Group Bid Amount': '1.50',
        'Ad Group Bid Strategy': 'AUTO',
        'Ad Group Placements': 'ALL_ON_TWITTER',
        'Targeting Gender': gender,
        'Targeting Age': age,
        'Targeting Locations': locations,
        'Targeting Languages': languages,
        'Tweet Text': tweetText,
        'Media URL': mediaAsset?.public_url || '',
        'Media Type': mediaAsset ? mediaType : '',
        'Card URL': data.ad_copy?.link_url || '',
      });
    }

    return rows;
  }

  getHeaders(): string[] {
    return TWITTER_HEADERS;
  }

  /**
   * Compose tweet text from campaign data
   * Combines headline and body, respecting 280 char limit
   */
  private composeTweetText(data: CampaignData): string {
    const parts: string[] = [];

    // Add headline
    if (data.ad_copy?.headline) {
      parts.push(data.ad_copy.headline);
    }

    // Add body text
    if (data.ad_copy?.body_text) {
      parts.push(data.ad_copy.body_text);
    }

    // Add URL if space allows (Twitter shortens URLs to 23 chars)
    let text = parts.join('\n\n');

    if (data.ad_copy?.link_url && text.length < 250) {
      text += '\n\n' + data.ad_copy.link_url;
    }

    // Truncate if needed
    if (text.length > 280) {
      text = text.slice(0, 277) + '...';
    }

    return text;
  }

  /**
   * Compose tweet text from ad variation data
   * Uses ad-specific content with fallback to campaign data
   */
  private composeTweetTextFromAd(ad: any, data: CampaignData): string {
    const parts: string[] = [];

    // Add headline from ad or fallback to campaign
    const headline = ad.headlines?.[0] || data.ad_copy?.headline;
    if (headline) {
      parts.push(headline);
    }

    // Add primary text from ad or fallback to body text
    const primaryText = Array.isArray(ad.primary_text)
      ? ad.primary_text[0]
      : ad.primary_text || data.ad_copy?.body_text;
    if (primaryText) {
      parts.push(primaryText);
    }

    // Add URL if space allows
    let text = parts.join('\n\n');
    const linkUrl = ad.link_url || ad.landing_page || data.ad_copy?.link_url;

    if (linkUrl && text.length < 250) {
      text += '\n\n' + linkUrl;
    }

    // Truncate if needed
    if (text.length > 280) {
      text = text.slice(0, 277) + '...';
    }

    return text;
  }

  /**
   * Format gender targeting for Twitter
   */
  private formatGender(genders?: string[]): string {
    if (!genders || genders.length === 0 || genders.includes('all')) {
      return 'ANY';
    }

    const hasM = genders.some((g) => g.toLowerCase() === 'male');
    const hasF = genders.some((g) => g.toLowerCase() === 'female');

    if (hasM && hasF) return 'ANY';
    if (hasM) return 'MALE';
    if (hasF) return 'FEMALE';
    return 'ANY';
  }

  /**
   * Format age range for Twitter
   * Twitter uses age buckets: AGE_13_17, AGE_18_24, AGE_25_34, AGE_35_49, AGE_50_54, AGE_55_64, AGE_65_AND_UP
   */
  private formatAgeRange(ageMin?: number, ageMax?: number): string {
    if (!ageMin && !ageMax) return '';

    const min = ageMin || 18;
    const max = ageMax || 65;

    // Map to Twitter age buckets
    const buckets: string[] = [];

    if (min <= 17 && max >= 13) buckets.push('AGE_13_17');
    if (min <= 24 && max >= 18) buckets.push('AGE_18_24');
    if (min <= 34 && max >= 25) buckets.push('AGE_25_34');
    if (min <= 49 && max >= 35) buckets.push('AGE_35_49');
    if (min <= 54 && max >= 50) buckets.push('AGE_50_54');
    if (min <= 64 && max >= 55) buckets.push('AGE_55_64');
    if (max >= 65) buckets.push('AGE_65_AND_UP');

    return buckets.join(';');
  }

  /**
   * Format date to ISO 8601 for Twitter
   */
  private formatDateTime(dateStr?: string): string {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toISOString();
    } catch {
      return dateStr;
    }
  }
}
